import { Camera, Wrench, Monitor, PartyPopper, Gamepad2, Flower2, BookOpen, MoreHorizontal } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { categoryHref } from "@/lib/categories";

const categories = [
  { name: "Cameras", icon: Camera, color: "from-blue-500/20 to-purple-500/20" },
  { name: "Tools", icon: Wrench, color: "from-blue-500/20 to-purple-500/20" },
  { name: "Electronics", icon: Monitor, color: "from-blue-500/20 to-purple-500/20" },
  { name: "Party Equipment", icon: PartyPopper, color: "from-blue-500/20 to-purple-500/20" },
  { name: "Gaming", icon: Gamepad2, color: "from-blue-500/20 to-purple-500/20" },
  { name: "Lawn & Garden", icon: Flower2, color: "from-blue-500/20 to-purple-500/20" },
  { name: "School Supplies", icon: BookOpen, color: "from-blue-500/20 to-purple-500/20" },
  { name: "Other", icon: MoreHorizontal, color: "from-blue-500/20 to-purple-500/20" },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 25, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1] as const
    }
  }
};

interface CategoryCardProps {
  category: {
    name: string;
    icon: React.ElementType;
    color: string;
  };
}

const CategoryCard = ({ category }: CategoryCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <motion.div 
      variants={itemVariants}
      whileHover={{ 
        scale: 1.05,
        rotateY: 5,
        rotateX: -5,
      }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      style={{ transformStyle: "preserve-3d", perspective: 1000 }}
    >
      <Link to={categoryHref(category.name)}>
        <div
          ref={cardRef}
          onMouseMove={handleMouseMove}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          className="relative group"
        >
          {/* Glow effect */}
          <div 
            className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 blur-md"
            style={{
              background: isHovering 
                ? `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, hsl(var(--primary) / 0.4), transparent 50%)`
                : 'transparent'
            }}
          />
          
          <Card 
            className="relative p-8 flex flex-col items-center justify-center gap-4 bg-card border-border/40 shadow-soft cursor-pointer rounded-2xl overflow-hidden transition-all duration-300 group-hover:shadow-3d-hover group-hover:border-primary/30"
          >
            {/* Animated gradient background */}
            <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-primary-foreground`} />
            
            {/* Icon container with 3D lift */}
            <motion.div 
              className="relative p-4 rounded-2xl bg-primary/8 group-hover:bg-primary/15 transition-all duration-300 z-10"
              whileHover={{ 
                scale: 1.15, 
                rotate: 8,
                boxShadow: "0 10px 30px -10px hsl(var(--primary) / 0.3)"
              }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <category.icon className="h-8 w-8 text-primary icon-hover-bounce" />
            </motion.div>
            
            <h3 className="relative text-base font-semibold text-center text-foreground z-10 group-hover:text-primary transition-colors duration-300">
              {category.name}
            </h3>

            {/* Shine effect on hover */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none"
              style={{
                background: `linear-gradient(135deg, transparent 40%, hsl(var(--primary) / 0.2) 50%, transparent 60%)`,
                backgroundSize: "200% 200%",
                animation: isHovering ? "shimmer 1.5s ease-in-out" : "none"
              }}
            />
          </Card>
        </div>
      </Link>
    </motion.div>
  );
};

const Categories = () => {
  const sectionRef = useRef<HTMLElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const contentY = useTransform(scrollYProgress, [0, 1], [0, 25]);
  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.55, 0.85], [0, 1, 1, 0]);

  return (
    <section ref={sectionRef} className="relative py-24 overflow-hidden" style={{ background: "linear-gradient(to bottom, #ffffff 0%, #e5e1fb 50%, #ffffff 100%)" }}>
      {/* Floating orbs for depth */}
      <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-primary/10 blur-3xl floating-orb" />
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-accent/10 blur-3xl floating-orb stagger-2" />
      
      <motion.div style={{ y: contentY, opacity }} className="container px-4 sm:px-6 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            Popular Categories
          </h2>
        </motion.div>
        
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-50px" }}
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6 max-w-6xl mx-auto"
          style={{ perspective: 1000 }}
        >
          {categories.map((category) => (
            <CategoryCard key={category.name} category={category} />
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Categories;
